import { ChangeEvent, ReactNode } from 'react';
import { FormikErrors, FormikTouched } from 'formik';
import { AnyObjectSchema } from 'yup';
import { AlertColor } from '@mui/material';

export enum STATUS {
  PENDING = 'PENDING',
  CREATED = 'CREATED',
  FAILED = 'FAILED',
  EXPIRED = 'EXPIRED'
}

export type ReportMetadata = unknown;

export interface CreatedReport {
  id: string;
  name: string;
  metadata: ReportMetadata;
  status: STATUS.CREATED;
  createDate: string;
  orderDate: string;
  expireDate: string;
  reportFileSize: number;
  progress?: number;
}

export interface PendingReport {
  id: string;
  name: string;
  metadata: ReportMetadata;
  status: STATUS.PENDING;
  orderDate: string;
  progress?: number;
}

export interface FailedReport {
  id: string;
  name: string;
  metadata: ReportMetadata;
  status: STATUS.FAILED;
  orderDate: string;
  progress?: number;
}

export interface ExpiredReport {
  id: string;
  name: string;
  metadata: ReportMetadata;
  status: STATUS.EXPIRED;
  createDate: string;
  orderDate: string;
  expireDate: string;
  reportFileSize: number;
  progress?: number;
}

export type Report = CreatedReport | ExpiredReport | PendingReport | FailedReport;

export enum ReportsValidationStatus {
  ALLOWED = 'ALLOWED',
  PENDING_REPORTS_LIMIT_REACHED = 'PENDING_REPORTS_LIMIT_REACHED',
  REPORTS_LIMIT_REACHED = 'REPORTS_LIMIT_REACHED'
}

export interface ReportsData {
  reports: Report[];
  limits: {
    status: ReportsValidationStatus;
    pendingLimit: {
      itemsCount: number;
      limit: number;
    };
    activeLimit: {
      itemsCount: number;
      limit: number;
    };
  };
}

export interface TableData {
  id: string;
  name: string;
  size: number | undefined | string;
  expireDate: string | undefined;
  status: ReactNode;
  progress?: number;
  actions: ReactNode;
}

export type HandleGenerateReport<T> = (values: T) => void;

export type Action = 'delete' | 'details' | 'download' | 'cancel' | 'regenerate' | 'retry';

export interface Locales {
  title: string;
  tableHeaderActionTooltip: string;
  dialog: {
    title: {
      generateReport: string;
      deleteReport: string;
      cancelReport: string;
      regenerateReport: string;
      retryReport: string;
    };
  };
  tooltip: {
    generateReport: string;
    cancel: string;
    delete: string;
    details: string;
    download: string;
    regenerate: string;
    retry: string;
  };
  reportName: {
    label: string;
    placeholder: string;
  };
  warningMsg: string;
}

export type HandleDeleteReportRequest = (id: string) => Promise<Response>;
export type HandleCancelReportRequest = (id: string) => Promise<Response>;
export type HandleRetryReportRequest = (id: string) => Promise<Response>;
export type HandleRegenerateReportRequest = (id: string) => Promise<Response>;
export type HandleGenerateReportRequest<T> = (value: T) => Promise<Response>;

export interface FiltersFormValues<T> {
  values: T;
  errors: FormikErrors<T>;
  touched: FormikTouched<T>;
  handleChange: (field: string, value: unknown) => void;
  handleBlur: (e: ChangeEvent) => void;
  locales: Locales;
  generatedBusinessError?: ReactNode;
  generatedBusinessErrorSeverity?: AlertColor;
}

export type FiltersFormType<T> = ({ values, errors, touched, handleChange }: FiltersFormValues<T>) => ReactNode;

export type FiltersInitialData = Record<string, unknown> & { name: string };

export interface Filters<T = unknown> {
  form: FiltersFormType<T>;
  clearValues: () => void;
  validation: AnyObjectSchema;
  details: (filters: ReportMetadata) => ReactNode;
  summary: (filters: ReportMetadata) => ReactNode;
  initialValues: Record<string, unknown> & { name: string };
  predefinedReportGeneration: boolean;
}
